import {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import SoFormStepConfiguration from './steps/configuration/SoFormStepConfiguration';
import loadConstants from 'utils/loads/constants';
import { formatters as addressFormatters } from 'utils/addresses';
import { useSelector } from 'react-redux';
import { selectors as authSelectors } from 'state/auth';
import api from 'api/api';
import SoFormStepPrice from './steps/price/SoFormStepPrice';
import SoFormStepComplements from './steps/complements/SoFormStepComplements';
import SoFormStepConfirmation from './steps/confirmation/SoFormStepConfirmation';
import SoFormStepSuccess from './steps/success/SoFormStepSuccess';
import routes from 'utils/routes';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { push } from 'connected-react-router';
import { validators as loadValidators } from 'utils/loads';
import { actions as orderActions } from 'state/order';
import { constants as orderConstants } from 'utils/order';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import { useDispatch } from 'react-redux';
import SoFormStepCard from './steps/card/SoFormStepCard';
import { actions as uiActions } from 'state/ui';

import { selectors as shipmentsSelectors } from 'state/shipments';
import { session } from 'state/analytics/events/form';
import keyGenerator from 'utils/keys';
import getMaximumOfLoads from 'utils/loads/methods/getMaximumOfLoads';
import { captureCatalogError } from 'sentry';
import { actions as appActions } from 'state/app';
import { selectors as paymentDataSelectors } from 'state/paymentData';
import validatePaymentData from 'utils/paymentData/validators/validatePaymentData';
import { isEmpty } from 'utils/types';
import SoFormStepPayment from './steps/payment/SoFormStepPayment';
import { isFlexLikeProduct } from './productTypes';
const LAST_WORKING_HOUR = 18;
const DEFAULT_MAX_LOADS = 33;

const isObjectEmpty = (objectName) => {
  return Object.keys(objectName).length === 0;
};

const getDefaultLoad = () => ({
  total_weight: '',
  item_height: '',
  item_width: 80,
  item_length: 120,
  load_type: 'PALLET',
  pallet_type: 'EU_PALLET',
  number_of_items: 1,
  temperature_range: null,
  loadKey: 'FIRST',
  maxLoads: DEFAULT_MAX_LOADS,
});

const getInitialStepsState = () => ({
  currentStep: SoFormStepConfiguration,
  //
  form_id: uuidv4(),
  session_id: session.get() || uuidv4(),
  //
  //
  //
  activeQuote: null,
  activeQuotePayload: null,
  //
  //
  configuration: {
    address_from: null,
    address_to: null,
    loads: [getDefaultLoad()],
    extras: [],
    isLoading: false,
    showConfigurationErrors: false,
    showNoProductsForRoute: false,
  },
  price: {
    candidates: [],
    selectedProductType: null,
    isLoading: false,
    showPriceErrors: false,
    quotePayload: null,
    showZipcodeError: false,
    use6hoursWindow: false,
  },
  complements: {
    isLoading: false,
    //
    add_internal_reference: false,
    internal_reference: '',
    //
    add_comment: false,
    pickup_comment_from_shipper_to_driver: '',
    dropoff_comment_from_shipper_to_driver: '',
    //
    add_insurance: false,
    insured_for: 0,
    //
    add_handling_loading: false,
    add_handling_unloading: false,
    price_per_handling: null,
  },
  confirmation: {
    isLoading: false,
  },
  payment: {
    isLoading: false,
  },
  card: {
    id: null,
    paymentIntentSecret: null,
  },
  success: {
    id: null,
  },
});

const SoFormStepsStateContext = createContext(null);
SoFormStepsStateContext.displayName = 'SoFormStepsStateContext';

export const useSoFormStepsState = () => {
  const ctx = useContext(SoFormStepsStateContext);

  if (typeof ctx === 'undefined') {
    throw new Error(
      'useSoFormStepsState must be used within SoFormStepsStateContext.Provider'
    );
  }

  return ctx;
};

const SoFormStepsApiContext = createContext(null);
SoFormStepsApiContext.displayName = 'SoFormStepsApiContext';

export const useSoFormStepsApi = () => {
  const ctx = useContext(SoFormStepsApiContext);

  if (typeof ctx === 'undefined') {
    throw new Error(
      'useSoFormStepsApi must be used within SoFormStepsApiContext.Provider'
    );
  }

  return ctx;
};

//
// configuration
//

const getValidLoads = ({
  shipmentsForQuote,
  settings,
  validations,
  orderType,
}) => {
  let validLoads = [];
  Object.keys(shipmentsForQuote).forEach((key) => {
    const shipmentLoads = shipmentsForQuote[key].loads;
    const shipmentValidLoads = shipmentLoads.filter((load) => {
      const { errors } = loadValidators.validateLoad({
        load,
        settings,
        validations,
        orderType,
        onlyDirty: false,
      });
      const numberOfErrors = Object.keys(errors).length;
      const isLoadValid = numberOfErrors === 0;
      return isLoadValid;
    });
    validLoads.push(...shipmentValidLoads);
  });
  return validLoads;
};

const getConfigurationErrors = ({
  state,
  redux_address_from,
  redux_address_to,
}) => {
  const configuration = state.configuration;

  const fieldsWithEmptyError = [];
  const fieldsWithInvalidError = [];

  //
  // addresses
  //

  if (isObjectEmpty(redux_address_from)) {
    fieldsWithEmptyError.push('address_from');
  }
  if (isObjectEmpty(redux_address_to)) {
    fieldsWithEmptyError.push('address_to');
  }

  //
  //   loads
  //

  const loads = configuration.loads;
  loads.forEach((load) => {
    //   number of items
    const number_of_items = load.number_of_items;
    const maxLoads = load.maxLoads;
    if (
      number_of_items === '0' ||
      number_of_items === 0 ||
      number_of_items === '' ||
      number_of_items === undefined
    ) {
      fieldsWithEmptyError.push({
        field: 'number_of_items',
        loadKey: load.loadKey,
      });
    } else if (number_of_items > maxLoads) {
      fieldsWithInvalidError.push({
        field: 'number_of_items',
        loadKey: load.loadKey,
      });
    }

    //   length
    const item_length = load.item_length;

    const maxLength =
      loadConstants.FIELDS_CONFIG[loadConstants.FIELDS.ITEM_LENGTH].MAX;
    const minLength =
      loadConstants.FIELDS_CONFIG[loadConstants.FIELDS.ITEM_LENGTH].MIN;

    if (
      item_length === '0' ||
      item_length === 0 ||
      item_length === '' ||
      item_length === undefined
    ) {
      fieldsWithEmptyError.push({
        field: 'item_length',
        loadKey: load.loadKey,
      });
    } else if (item_length > maxLength || item_length < minLength) {
      fieldsWithInvalidError.push({
        field: 'item_length',
        loadKey: load.loadKey,
      });
    }

    //   width
    const item_width = load.item_width;

    const maxWidth =
      loadConstants.FIELDS_CONFIG[loadConstants.FIELDS.ITEM_WIDTH].MAX;
    const minWidth =
      loadConstants.FIELDS_CONFIG[loadConstants.FIELDS.ITEM_WIDTH].MIN;

    if (
      item_width === '0' ||
      item_width === 0 ||
      item_width === '' ||
      item_width === undefined
    ) {
      fieldsWithEmptyError.push({ field: 'item_width', loadKey: load.loadKey });
    } else if (item_width > maxWidth || item_width < minWidth) {
      fieldsWithInvalidError.push({
        field: 'item_width',
        loadKey: load.loadKey,
      });
    }

    //   height
    const item_height = load.item_height;

    const maxHeight =
      loadConstants.FIELDS_CONFIG[loadConstants.FIELDS.ITEM_HEIGHT].MAX;
    const minHeight =
      loadConstants.FIELDS_CONFIG[loadConstants.FIELDS.ITEM_HEIGHT].MIN;

    if (
      item_height === '0' ||
      item_height === 0 ||
      item_height === '' ||
      item_height === undefined
    ) {
      fieldsWithEmptyError.push({
        field: 'item_height',
        loadKey: load.loadKey,
      });
    } else if (item_height > maxHeight || item_height < minHeight) {
      fieldsWithInvalidError.push({
        field: 'item_height',
        loadKey: load.loadKey,
      });
    }

    //   weight
    const total_weight = load.total_weight;
    const total_weight_for_all_loads = configuration.loads.reduce(
      (accumulator, currentValue) => accumulator + currentValue.total_weight,
      0
    );
    const maxWeight = 24000;

    if (
      total_weight === '0' ||
      total_weight === 0 ||
      total_weight === '' ||
      total_weight === undefined
    ) {
      fieldsWithEmptyError.push({
        field: 'total_weight',
        loadKey: load.loadKey,
      });
    } else if (total_weight_for_all_loads > maxWeight) {
      fieldsWithInvalidError.push({
        field: 'total_weight',
        loadKey: load.loadKey,
      });
    }
  });

  // recap

  const hasErrors =
    fieldsWithEmptyError.length + fieldsWithInvalidError.length > 0;

  return {
    fieldsWithEmptyError,
    fieldsWithInvalidError,
    hasErrors,
  };
};

const getPayloadForCatalog = ({
  stepsState,
  userId,
  shipperId,
  redux_address_from,
  redux_address_to,
}) => {
  const payload = {
    shipments: [
      {
        extras: [],
        numLoads: 0,
        internal_reference: '',
        pickup_comment_from_shipper_to_driver: '',
        dropoff_comment_from_shipper_to_driver: '',
        shipmentKey: 'FIRST_SHIPMENT',
        load_extra_info: '',
        dropoff_appointment_required: false,
      },
    ],
    repeated_from: null,
    main_quote: true,
    extras: [],
    price_type: 'DIRECT',
    internal_price_type: 'DIRECT',
    x_docking_enabled: false,
  };

  //   data
  payload.form_id = stepsState.form_id;
  payload.session_id = stepsState.session_id;

  payload.user_id = userId;
  payload.shipper_id = shipperId;

  payload.shipments[0].address_from = redux_address_from;
  payload.shipments[0].address_from_raw =
    addressFormatters.getAddressLabel(redux_address_from);

  payload.shipments[0].address_to = redux_address_to;
  payload.shipments[0].address_to_raw =
    addressFormatters.getAddressLabel(redux_address_to);

  payload.shipments[0].loads = stepsState.configuration.loads;
  payload.shipments[0].loads = stepsState.configuration.loads;
  payload.shipments[0].loads[0].loadKey = 'FIRST';
  payload.shipments[0].shipmentKey = 'FIRST_SHIPMENT';

  payload.shipments[0].pickup_timezone = redux_address_from.timezone;
  payload.shipments[0].dropoff_timezone = redux_address_to.timezone;

  payload.extras = stepsState.configuration.extras;

  return payload;
};

const getCandidates = async (payload) => {
  const response = await api.getCatalog({ params: payload });
  const data = response.data;
  const { candidates, catalog_request_id } = data;
  const sortValues = {
    ECONOMY: 1,
    STANDARD: 3,
    EXPRESS: 5,
    PALLET_NETWORK: 7,
  };

  const sortedCandidates = candidates.sort((a, b) => {
    const orderA = sortValues[a.productType];
    const orderB = sortValues[b.productType];
    return orderA - orderB;
  });

  return { candidates: sortedCandidates, catalog_request_id };
};

//
// price
//

const getPriceErrors = (state) => {
  const price = state.price;

  const fieldsWithEmptyError = [];
  const fieldsWithInvalidError = [];

  //
  // selectedProductType
  //

  if (price.selectedProductType === null) {
    fieldsWithEmptyError.push('selectedProductType');
  }

  // recap

  const hasErrors =
    fieldsWithEmptyError.length + fieldsWithInvalidError.length > 0;

  return {
    fieldsWithEmptyError,
    fieldsWithInvalidError,
    hasErrors,
  };
};

const convertProposedToDate = (dateString) => {
  return dateString.slice(0, 10);
};

const convertProposedToDateTime = (dateString) => {
  return dateString.replace('Z', '.000Z');
};

const getTimesWithHoursDifference = ({
  originalTimes,
  numberOfHours,
  operation,
}) => {
  const newTimes = { ...originalTimes };
  if (operation === '-') {
    newTimes.pickup_time_to = moment(originalTimes.pickup_time_to)
      .subtract(numberOfHours, 'hours')
      .toJSON();
    newTimes.dropoff_time_to = moment(originalTimes.dropoff_time_to)
      .subtract(numberOfHours, 'hours')
      .toJSON();
  } else if (operation === '+') {
    newTimes.pickup_time_to = moment(originalTimes.pickup_time_to)
      .add(numberOfHours, 'hours')
      .toJSON();
    newTimes.dropoff_time_to = moment(originalTimes.dropoff_time_to)
      .add(numberOfHours, 'hours')
      .toJSON();
  } else {
    console.error('invalid call to getTimesWithHoursDifference (operation)');
  }
  return newTimes;
};

const getQuotePayload = ({
  stepsState,
  userId,
  shipperId,
  candidate,
  redux_address_from,
  redux_address_to,
}) => {
  const initialPayload = getPayloadForCatalog({
    stepsState,
    userId,
    shipperId,
    redux_address_from,
    redux_address_to,
  });

  const proposedDatetimes = candidate.proposedDatetimes;

  const times = {};

  times.pickup_date_from = convertProposedToDate(proposedDatetimes.pickup_from);
  times.pickup_date_to = convertProposedToDate(proposedDatetimes.pickup_to);
  times.pickup_time_from = convertProposedToDateTime(
    proposedDatetimes.pickup_from
  );
  times.pickup_time_to = convertProposedToDateTime(proposedDatetimes.pickup_to);

  times.dropoff_date_from = convertProposedToDate(
    proposedDatetimes.dropoff_from
  );
  times.dropoff_date_to = convertProposedToDate(proposedDatetimes.dropoff_to);
  times.dropoff_time_from = convertProposedToDateTime(
    proposedDatetimes.dropoff_from
  );
  times.dropoff_time_to = convertProposedToDateTime(
    proposedDatetimes.dropoff_to
  );

  initialPayload.shipments[0] = { ...initialPayload.shipments[0], ...times };

  initialPayload.shipments[0].address_from = redux_address_from;
  initialPayload.shipments[0].address_to = redux_address_to;

  initialPayload.slug = candidate.proposedQuote.slug;

  const payloadWithConfiguration = {
    ...initialPayload,
  };

  return payloadWithConfiguration;
};

//
// confirmation
//

const getPayloadWithComplements = ({ stepsState, candidate }) => {
  const initialPayload = candidate.formData.quotePayload;

  const payloadWithComplements = { ...initialPayload };
  const complements = stepsState.complements;

  if (complements.add_internal_reference) {
    payloadWithComplements.shipments[0].internal_reference =
      complements.internal_reference;
  }

  if (complements.add_comment) {
    payloadWithComplements.shipments[0].pickup_comment_from_shipper_to_driver =
      complements.pickup_comment_from_shipper_to_driver;
    payloadWithComplements.shipments[0].dropoff_comment_from_shipper_to_driver =
      complements.dropoff_comment_from_shipper_to_driver;
  }

  if (complements.add_insurance && complements.insured_for > 0) {
    payloadWithComplements.extras.push('INSURANCE');
    payloadWithComplements.shipments[0].extras.push('INSURANCE');
    payloadWithComplements.shipments[0].insured_for = complements.insured_for;
  } else {
    payloadWithComplements.extras = payloadWithComplements.extras.filter(
      (e) => e !== 'INSURANCE'
    );
    payloadWithComplements.shipments[0].extras =
      payloadWithComplements.shipments[0].extras.filter(
        (e) => e !== 'INSURANCE'
      );
  }

  if (complements.add_handling_loading) {
    payloadWithComplements.shipments[0].extras.push('HANDLING_LOADING');
  } else {
    payloadWithComplements.shipments[0].extras =
      payloadWithComplements.shipments[0].extras.filter(
        (e) => e !== 'HANDLING_LOADING'
      );
  }

  if (complements.add_handling_unloading) {
    payloadWithComplements.shipments[0].extras.push('HANDLING_UNLOADING');
  } else {
    payloadWithComplements.shipments[0].extras =
      payloadWithComplements.shipments[0].extras.filter(
        (e) => e !== 'HANDLING_UNLOADING'
      );
  }

  return payloadWithComplements;
};

const confirmQuote = async (payload) => {
  const response = await api.confirmQuote({ params: payload });
  const data = response.data;
  return data;
};

//
// card
//

const initPaymentIntent = async (id) => {
  const { data } = await api.initPaymentIntent(id);
  return data?.client_secret;
};

export const SoFormContextProvider = ({ children }) => {
  const initialStepsState = useMemo(() => getInitialStepsState(), []);

  const [stepsState, setStepsState] = useState(initialStepsState);

  const dispatch = useDispatch();

  //   data for requests
  const shipperId = useSelector(authSelectors.getUserShipperId);
  const user = useSelector(authSelectors.getUser);
  const userId = user.id;
  const terms_and_conditions_version = useSelector(
    authSelectors.getCurrentTermsAndConditionsVersion
  );

  //   extended state
  const stateValue = { ...stepsState };

  //
  //   configuration
  //

  const getMaxLoads = useCallback(async () => {
    const loads = stepsState.configuration.loads;
    const validLoads = getValidLoads({
      shipmentsForQuote: {
        FIRST_SHIPMENT: {
          loads,
        },
      },
      settings: {},
      validations: {},
      orderType: 'CATALOG',
    });

    if (validLoads.length === 0) return;

    const loadsObject = validLoads.reduce((accumulator, currentLoad) => {
      accumulator[currentLoad.loadKey] = currentLoad;
      delete accumulator[currentLoad.loadKey].maxLoads;
      return accumulator;
    }, {});
    const payload = {
      shipments_order: [
        ['FIRST_SHIPMENT', 'PICKUP'],
        ['FIRST_SHIPMENT', 'DROPOFF'],
      ],
      shipments_loads: { FIRST_SHIPMENT: loadsObject },
    };

    try {
      const data = await getMaximumOfLoads({
        shipmentsWithLoads: payload.shipments_loads,
        shipmentsInOrder: payload.shipments_order,
      });

      const firstShipmentMaxLoads = data.FIRST_SHIPMENT;
      const newState = { ...stepsState };
      newState.configuration.loads = newState.configuration.loads.map(
        (currentLoad) => {
          if (firstShipmentMaxLoads[currentLoad.loadKey] === undefined)
            return currentLoad;
          return {
            ...currentLoad,
            maxLoads: firstShipmentMaxLoads[currentLoad.loadKey],
          };
        }
      );
      setStepsState(newState);
    } catch (e) {
      captureCatalogError({
        e,
        step_name: SoFormStepConfiguration.nameForData,
        error_type: 'API',
        action_name: 'getMaxLoads',
        extras: payload,
      });
    }
  }, [stepsState]);

  const redux_address_from = useSelector((state) =>
    shipmentsSelectors.getShipmentAddress(state, 'FIRST_SHIPMENT', 'pickup')
  );
  const redux_address_to = useSelector((state) =>
    shipmentsSelectors.getShipmentAddress(state, 'FIRST_SHIPMENT', 'dropoff')
  );

  const isZipcode = !!(
    redux_address_from?.isZipcode || redux_address_to?.isZipcode
  );

  stateValue.configuration.isZipcode = isZipcode;

  stateValue.configuration.errors = getConfigurationErrors({
    state: stepsState,
    redux_address_from,
    redux_address_to,
  });

  const hideNoProductsForRoute = useCallback(() => {
    const newState = { ...stepsState };
    newState.configuration.showNoProductsForRoute = false;
    setStepsState(newState);
  }, [stepsState]);

  const setAddress = useCallback(
    ({ field, value }) => {
      const stateWithAddress = { ...stepsState };
      stateWithAddress.configuration[field] = value;
      stateWithAddress.configuration.showNoProductsForRoute = false;
      setStepsState(stateWithAddress);
    },
    [stepsState]
  );

  const addLoad = useCallback(() => {
    const newState = { ...stepsState };
    const newLoad = getDefaultLoad();
    newLoad.loadKey = keyGenerator.timestamp();
    newState.configuration.loads.push(newLoad);
    setStepsState(newState);
    getMaxLoads();
  }, [getMaxLoads, stepsState]);

  const deleteLoad = useCallback(
    (loadToDelete) => {
      const newState = { ...stepsState };

      newState.configuration.loads = newState.configuration.loads.filter(
        (load) => load.loadKey !== loadToDelete.loadKey
      );
      setStepsState(newState);
      getMaxLoads();
    },
    [getMaxLoads, stepsState]
  );

  const setLoadField = useCallback(
    ({ load, field, value }) => {
      const nextValue = isNaN(parseInt(value)) ? 0 : parseInt(value);
      const updatedLoad = { ...load };
      if (field === 'number_of_items') {
        const stateWithUpdatedLoad = { ...stepsState };
        updatedLoad[field] = nextValue;
        stateWithUpdatedLoad.configuration.loads =
          stateWithUpdatedLoad.configuration.loads.map((thisLoad) => {
            return thisLoad.loadKey === load.loadKey ? updatedLoad : thisLoad;
          });
        stateWithUpdatedLoad.configuration.showNoProductsForRoute = false;
        setStepsState(stateWithUpdatedLoad);
      } else {
        const { item_length, item_width, item_height, load_type } = load;
        const newSizes = { item_length, item_width, item_height };
        newSizes[field] = nextValue;
        updatedLoad[field] = nextValue;
        const newState = { ...stepsState };
        newState.configuration.showNoProductsForRoute = false;
        if (load_type === 'PALLET') {
          if (newSizes.item_length === 120 && newSizes.item_width === 80) {
            updatedLoad.pallet_type = 'EU_PALLET';
          } else if (
            newSizes.item_length === 120 &&
            newSizes.item_width === 100
          ) {
            updatedLoad.pallet_type = 'US_PALLET';
          } else {
            updatedLoad.pallet_type = 'CUSTOM_PALLET';
          }
        }
        newState.configuration.loads = newState.configuration.loads.map(
          (thisLoad) => {
            return thisLoad.loadKey === load.loadKey ? updatedLoad : thisLoad;
          }
        );
        setStepsState(newState);
      }
      getMaxLoads();
    },
    [getMaxLoads, stepsState]
  );

  const setLoadType = useCallback(
    ({ load, value }) => {
      const updatedLoad = {
        ...load,
      };
      if (value === 'PALLET') {
        updatedLoad.load_type = 'PALLET';
        updatedLoad.pallet_type = 'EU_PALLET';
        updatedLoad.item_length = 120;
        updatedLoad.item_width = 80;
      } else {
        updatedLoad.load_type = 'PARCEL';
        updatedLoad.pallet_type = 'CUSTOM_PALLET';
        updatedLoad.item_length = 60;
        updatedLoad.item_width = 40;
        updatedLoad.item_height = 40;
      }
      const newState = { ...stepsState };
      newState.configuration.loads = newState.configuration.loads.map(
        (thisLoad) => {
          return thisLoad.loadKey === load.loadKey ? updatedLoad : thisLoad;
        }
      );
      setStepsState(newState);
      getMaxLoads();
    },
    [getMaxLoads, stepsState]
  );

  const setPalletType = useCallback(
    ({ load, value }) => {
      const updatedLoad = {
        ...load,
      };
      if (value === 'CUSTOM_PALLET') {
        updatedLoad.pallet_type = 'CUSTOM_PALLET';
        updatedLoad.item_width = '';
        updatedLoad.item_length = '';
      } else if (value === 'EU_PALLET') {
        updatedLoad.pallet_type = 'EU_PALLET';
        updatedLoad.item_length = 120;
        updatedLoad.item_width = 80;
      } else if (value === 'US_PALLET') {
        updatedLoad.pallet_type = 'US_PALLET';
        updatedLoad.item_length = 120;
        updatedLoad.item_width = 100;
      } else if (value === 'BOX') {
        updatedLoad.load_type = 'PARCEL';
        updatedLoad.item_length = 60;
        updatedLoad.item_width = 40;
        updatedLoad.item_height = 40;
      } else if (value === 'CUSTOM_BOX') {
        updatedLoad.load_type = 'PARCEL';
        updatedLoad.item_length = '';
        updatedLoad.item_width = '';
        updatedLoad.item_height = '';
      } else if (value === 'BULK') {
        updatedLoad.load_type = 'BULK';
        updatedLoad.item_length = '';
        updatedLoad.item_width = '';
        updatedLoad.item_height = '';
      }
      const newState = { ...stepsState };
      newState.configuration.loads = newState.configuration.loads.map(
        (thisLoad) => {
          return thisLoad.loadKey === load.loadKey ? updatedLoad : thisLoad;
        }
      );
      setStepsState(newState);
      getMaxLoads();
    },
    [getMaxLoads, stepsState]
  );

  const addExtra = useCallback(
    (extraName) => {
      const stateWithExtraUpdated = { ...stepsState };
      stateWithExtraUpdated.configuration.extras.push(extraName);
      stateWithExtraUpdated.configuration.showNoProductsForRoute = false;
      setStepsState(stateWithExtraUpdated);
    },
    [stepsState]
  );

  const removeExtra = useCallback(
    (extraName) => {
      const stateWithExtraUpdated = { ...stepsState };
      stateWithExtraUpdated.configuration.extras =
        stateWithExtraUpdated.configuration.extras.filter(
          (i) => i !== extraName
        );
      stateWithExtraUpdated.configuration.showNoProductsForRoute = false;
      setStepsState(stateWithExtraUpdated);
    },
    [stepsState]
  );

  const getCandidatesWithFormData = ({
    candidates,
    catalog_request_id,
    stepsState,
    userId,
    shipperId,
    redux_address_from,
    redux_address_to,
  }) => {
    const candidatesWithFormData = candidates.map((candidate) => {
      // quote
      const quote = { ...candidate.proposedQuote };

      //   payload
      const quotePayload = getQuotePayload({
        stepsState,
        userId,
        shipperId,
        candidate,
        redux_address_from,
        redux_address_to,
      });

      //   catalog_request_id
      quotePayload.catalog_request_id = catalog_request_id;

      //   is updating after date/time change
      const isUpdating = false;

      // times
      const proposedDatetimes = candidate.proposedDatetimes;
      const minTime = proposedDatetimes.pickup_from;

      let minTimeForStandard = proposedDatetimes.pickup_from;
      // for standard / flex (and flex-like products), we change the min time
      if (isFlexLikeProduct(candidate.productType)) {
        const lastWorkingHourForToday = moment()
          .hours(LAST_WORKING_HOUR)
          .minutes(0)
          .seconds(0)
          .milliseconds(0);
        const isNowBeforeWorkingHours = moment().isBefore(
          lastWorkingHourForToday
        );
        if (isNowBeforeWorkingHours) {
          minTimeForStandard = moment()
            .add(1, 'day')
            .hours(0)
            .minutes(0)
            .seconds(0)
            .milliseconds(0);
        } else {
          minTimeForStandard = moment()
            .add(2, 'day')
            .hours(0)
            .minutes(0)
            .seconds(0)
            .milliseconds(0);
        }
      }

      const travelTimeInMinutes = null;

      //   formData is available inside each candidate
      const formData = {
        quote,
        quotePayload,
        isUpdating,
        minTime,
        minTimeForStandard,
        travelTimeInMinutes,
      };
      candidate.formData = formData;
      return candidate;
    });
    return candidatesWithFormData;
  };

  const onConfigurationSubmit = useCallback(async () => {
    if (stateValue.configuration.errors.hasErrors) {
      const stepsStateWithShowConfigurationErrors = { ...stepsState };
      stepsStateWithShowConfigurationErrors.configuration.showConfigurationErrors = true;
      setStepsState(stepsStateWithShowConfigurationErrors);
    } else {
      dispatch(
        analyticsActions.sendEvent(
          analyticsEvents.catalogStepContinue({
            from: SoFormStepConfiguration.nameForData,
            to: SoFormStepPrice.nameForData,
          })
        )
      );
      const payloadForCatalog = getPayloadForCatalog({
        stepsState,
        userId,
        shipperId,
        redux_address_from,
        redux_address_to,
      });
      const stepsStateWithConfigurationLoading = { ...stepsState };
      stepsStateWithConfigurationLoading.configuration.isLoading = true;
      setStepsState(stepsStateWithConfigurationLoading);
      try {
        const { candidates, catalog_request_id } = await getCandidates(
          payloadForCatalog
        );

        if (candidates.length > 0) {
          const stepsStateWithCandidates = { ...stepsState };

          const candidatesWithFormData = getCandidatesWithFormData({
            candidates,
            catalog_request_id,
            stepsState,
            userId,
            shipperId,
            redux_address_from,
            redux_address_to,
          });

          stepsStateWithCandidates.price.candidates = candidatesWithFormData;
          stepsStateWithCandidates.price.selectedProductType = null;
          stepsStateWithCandidates.configuration.isLoading = false;
          stepsStateWithCandidates.currentStep = SoFormStepPrice;
          setStepsState(stepsStateWithCandidates);
          candidatesWithFormData.forEach((c) => {
            dispatch(
              analyticsActions.sendEvent(
                analyticsEvents.form.priceQuoteShown({
                  quoteBody: c.formData.quotePayload,
                  quoteResponse: c.formData.quote,
                  isEditing: false,
                  type: orderConstants.DIRECT_ORDER,
                  pricing_strategy: c.pricing_strategy,
                  productType: c.productType,
                })
              )
            );
          });
        } else {
          const stepsStateWithNoCandidates = { ...stepsState };
          stepsStateWithNoCandidates.price.candidates = [];
          stepsStateWithNoCandidates.price.selectedProductType = null;
          stepsStateWithNoCandidates.configuration.isLoading = false;
          stepsStateWithNoCandidates.configuration.showNoProductsForRoute = true;
          setStepsState(stepsStateWithNoCandidates);
          dispatch(analyticsActions.sendEvent(analyticsEvents.badRouteError()));
        }
      } catch (e) {
        captureCatalogError({
          e,
          step_name: SoFormStepConfiguration.nameForData,
          error_type: 'API',
          action_name: 'onConfigurationSubmit getCandidates',
          extras: payloadForCatalog,
        });
      }
    }
  }, [
    stateValue.configuration.errors.hasErrors,
    stepsState,
    userId,
    shipperId,
    redux_address_from,
    redux_address_to,
    dispatch,
  ]);

  //
  // price
  //

  stateValue.price.errors = getPriceErrors(stepsState);

  const setShowZipcodeError = useCallback(() => {
    const newState = { ...stepsState };
    newState.price.showZipcodeError = true;
    setStepsState(newState);
  }, [stepsState]);

  const selectProduct = useCallback(
    async (candidate) => {
      const newState = { ...stepsState };
      const selectedProductType = candidate.productType;
      newState.price.selectedProductType = selectedProductType;
      setStepsState(newState);
      const number_of_items = stepsState.configuration.loads.reduce(
        (accumulator, currentValue) =>
          accumulator + currentValue.number_of_items,
        0
      );
      const payloadForShipperWindows = {
        shipments: [
          {
            address_from: redux_address_from,
            address_to: redux_address_to,
            pickup_time_from: candidate.proposedDatetimes.pickup_from,
            pickup_time_to: candidate.proposedDatetimes.pickup_to,
            load: {
              number_of_items,
            },
          },
        ],
        request_time: moment().format(),
      };

      try {
        const timeWindowsResponse = await api.getTimeWindows({
          shipments: payloadForShipperWindows.shipments,
          request_time: payloadForShipperWindows.request_time,
        });

        const timeWindows = timeWindowsResponse.data;

        const originalPickupFrom =
          payloadForShipperWindows.shipments[0].pickup_time_from;
        const proposedDropoffFrom = timeWindows[0].dropoff_time_from_min;

        const travelTimeInMinutes = Math.ceil(
          moment
            .duration(
              moment(proposedDropoffFrom).diff(moment(originalPickupFrom))
            )
            .asMinutes()
        );

        const candidateWithTravelTime = {
          ...candidate,
        };
        candidateWithTravelTime.formData.travelTimeInMinutes =
          travelTimeInMinutes;
        const newStateWithTravelTime = {
          ...newState,
        };
        newStateWithTravelTime.price.candidates =
          newStateWithTravelTime.price.candidates.map((c) => {
            if (c.productType !== selectedProductType) return c;
            else {
              return candidateWithTravelTime;
            }
          });
        setStepsState(newStateWithTravelTime);
      } catch (e) {
        captureCatalogError({
          e,
          step_name: SoFormStepPrice.nameForData,
          error_type: 'API',
          action_name: 'selectProduct getTimeWindows',
          extras: payloadForShipperWindows,
        });
      }
    },
    [redux_address_from, redux_address_to, stepsState]
  );

  const setUse6hoursWindow = useCallback(
    async ({ value, candidate }) => {
      const {
        pickup_time_from,
        pickup_time_to,
        dropoff_time_from,
        dropoff_time_to,
      } = candidate.formData.quotePayload.shipments[0];
      const originalTimes = {
        pickup_time_from,
        pickup_time_to,
        dropoff_time_from,
        dropoff_time_to,
      };
      const newTimes = getTimesWithHoursDifference({
        originalTimes,
        numberOfHours: 2,
        operation: value ? '-' : '+',
      });
      const newState = { ...stepsState };
      newState.price.use6hoursWindow = value;
      const selectedProductType = stepsState.price.selectedProductType;
      const candidateWithUpdatedTimes = { ...candidate };
      candidateWithUpdatedTimes.formData.isUpdating = true;
      Object.assign(
        candidateWithUpdatedTimes.formData.quotePayload.shipments[0],
        newTimes
      );
      newState.price.candidates = newState.price.candidates.map((c) => {
        if (c.productType !== selectedProductType) return c;
        else {
          return candidateWithUpdatedTimes;
        }
      });
      newState.price.isLoading = true;
      setStepsState(newState);

      // get new quote and disable loading / updating after
      const newStateWithQuoteUpdated = { ...newState };
      const quotes = [candidateWithUpdatedTimes.formData.quotePayload];
      try {
        const response = await api.doQuote(quotes);
        const updatedQuote = response.data.quotes.DIRECT;
        candidateWithUpdatedTimes.formData.quote = updatedQuote;
        candidateWithUpdatedTimes.formData.quotePayload.slug =
          updatedQuote.slug;
        candidateWithUpdatedTimes.formData.isUpdating = false;
        newStateWithQuoteUpdated.price.isLoading = false;
        newStateWithQuoteUpdated.price.candidates =
          newStateWithQuoteUpdated.price.candidates.map((c) => {
            if (c.productType !== selectedProductType) return c;
            else {
              return candidateWithUpdatedTimes;
            }
          });
        setStepsState(newStateWithQuoteUpdated);
      } catch (e) {
        captureCatalogError({
          e,
          step_name: SoFormStepPrice.nameForData,
          error_type: 'API',
          action_name: 'setUse6hoursWindow doQuote',
          extras: quotes,
        });
      }
    },
    [stepsState]
  );

  const setEconomyRange = useCallback(
    async ({ newTimes, candidate }) => {
      const newState = { ...stepsState };
      const selectedProductType = stepsState.price.selectedProductType;
      const candidateWithUpdatedTimes = { ...candidate };
      candidateWithUpdatedTimes.formData.isUpdating = true;
      Object.assign(
        candidateWithUpdatedTimes.formData.quotePayload.shipments[0],
        newTimes
      );
      newState.price.candidates = newState.price.candidates.map((c) => {
        if (c.productType !== selectedProductType) return c;
        else {
          return candidateWithUpdatedTimes;
        }
      });
      newState.price.isLoading = true;
      setStepsState(newState);

      // get new quote and disable loading / updating after
      const newStateWithQuoteUpdated = { ...newState };
      const quotes = [candidateWithUpdatedTimes.formData.quotePayload];
      try {
        const response = await api.doQuote(quotes);
        let updatedQuote = response.data.quotes.DIRECT;
        if (updatedQuote === undefined)
          updatedQuote = response.data.quotes.TODAY;
        candidateWithUpdatedTimes.formData.quote = updatedQuote;
        candidateWithUpdatedTimes.formData.quotePayload.slug =
          updatedQuote.slug;
        candidateWithUpdatedTimes.formData.isUpdating = false;
        newStateWithQuoteUpdated.price.isLoading = false;
        newStateWithQuoteUpdated.price.candidates =
          newStateWithQuoteUpdated.price.candidates.map((c) => {
            if (c.productType !== selectedProductType) return c;
            else {
              return candidateWithUpdatedTimes;
            }
          });
        setStepsState(newStateWithQuoteUpdated);
      } catch (e) {
        captureCatalogError({
          e,
          step_name: SoFormStepPrice.nameForData,
          error_type: 'API',
          action_name: 'setEconomyRange doQuote',
          extras: quotes,
        });
      }
    },
    [stepsState]
  );

  const setExpressHours = useCallback(
    async ({ newTimes, candidate }) => {
      const newState = { ...stepsState };
      const selectedProductType = stepsState.price.selectedProductType;
      const candidateWithUpdatedTimes = { ...candidate };
      candidateWithUpdatedTimes.formData.isUpdating = true;

      Object.assign(
        candidateWithUpdatedTimes.formData.quotePayload.shipments[0],
        newTimes
      );

      const candidateShipment =
        candidateWithUpdatedTimes.formData.quotePayload.shipments[0];

      candidateShipment.pickup_date_from = moment(
        candidateShipment.pickup_time_from
      ).format('YYYY-MM-DD');
      candidateShipment.pickup_date_to = moment(
        candidateShipment.pickup_time_to
      ).format('YYYY-MM-DD');
      candidateShipment.dropoff_date_from = moment(
        candidateShipment.dropoff_time_from
      ).format('YYYY-MM-DD');
      candidateShipment.dropoff_date_to = moment(
        candidateShipment.dropoff_time_to
      ).format('YYYY-MM-DD');

      newState.price.candidates = newState.price.candidates.map((c) => {
        if (c.productType !== selectedProductType) return c;
        else {
          return candidateWithUpdatedTimes;
        }
      });
      newState.price.isLoading = true;
      setStepsState(newState);

      // get new quote and disable loading / updating after
      const newStateWithQuoteUpdated = { ...newState };
      const payload = candidateWithUpdatedTimes.formData.quotePayload;
      payload.internal_price_type = 'TODAY';
      const quotes = [payload];
      try {
        const response = await api.doQuote(quotes);
        let updatedQuote = response.data.quotes.TODAY;
        if (updatedQuote === undefined)
          updatedQuote = response.data.quotes.DIRECT;
        candidateWithUpdatedTimes.formData.quote = updatedQuote;
        candidateWithUpdatedTimes.formData.quotePayload.slug =
          updatedQuote.slug;
        candidateWithUpdatedTimes.formData.isUpdating = false;
        newStateWithQuoteUpdated.price.isLoading = false;
        newStateWithQuoteUpdated.price.candidates =
          newStateWithQuoteUpdated.price.candidates.map((c) => {
            if (c.productType !== selectedProductType) return c;
            else {
              return candidateWithUpdatedTimes;
            }
          });
        setStepsState(newStateWithQuoteUpdated);
      } catch (e) {
        captureCatalogError({
          e,
          step_name: SoFormStepPrice.nameForData,
          error_type: 'API',
          action_name: 'setExpressHours doQuote',
          extras: quotes,
        });
      }
    },
    [stepsState]
  );

  const setFlexHours = useCallback(
    async ({ newTimes, candidate }) => {
      const newState = { ...stepsState };
      const selectedProductType = stepsState.price.selectedProductType;
      const candidateWithUpdatedTimes = { ...candidate };
      candidateWithUpdatedTimes.formData.isUpdating = true;

      Object.assign(
        candidateWithUpdatedTimes.formData.quotePayload.shipments[0],
        newTimes
      );

      const candidateShipment =
        candidateWithUpdatedTimes.formData.quotePayload.shipments[0];

      candidateShipment.pickup_date_from = moment(
        candidateShipment.pickup_time_from
      ).format('YYYY-MM-DD');
      candidateShipment.pickup_date_to = moment(
        candidateShipment.pickup_time_to
      ).format('YYYY-MM-DD');
      candidateShipment.dropoff_date_from = moment(
        candidateShipment.dropoff_time_from
      ).format('YYYY-MM-DD');
      candidateShipment.dropoff_date_to = moment(
        candidateShipment.dropoff_time_to
      ).format('YYYY-MM-DD');

      newState.price.candidates = newState.price.candidates.map((c) => {
        if (c.productType !== selectedProductType) return c;
        else {
          return candidateWithUpdatedTimes;
        }
      });
      newState.price.isLoading = true;
      setStepsState(newState);

      // get new quote and disable loading / updating after
      const newStateWithQuoteUpdated = { ...newState };
      const payload = candidateWithUpdatedTimes.formData.quotePayload;
      const quotes = [payload];
      try {
        const response = await api.doQuote(quotes);
        let updatedQuote = response.data.quotes.DIRECT;
        candidateWithUpdatedTimes.formData.quote = updatedQuote;
        candidateWithUpdatedTimes.formData.quotePayload.slug =
          updatedQuote.slug;
        candidateWithUpdatedTimes.formData.isUpdating = false;

        newStateWithQuoteUpdated.price.isLoading = false;
        newStateWithQuoteUpdated.price.candidates =
          newStateWithQuoteUpdated.price.candidates.map((c) => {
            if (c.productType !== selectedProductType) return c;
            else {
              return candidateWithUpdatedTimes;
            }
          });
        setStepsState(newStateWithQuoteUpdated);
      } catch (e) {
        captureCatalogError({
          e,
          step_name: SoFormStepPrice.nameForData,
          error_type: 'API',
          action_name: 'setFlexHours doQuote',
          extras: quotes,
        });
      }
    },
    [stepsState]
  );

  const onPriceSubmit = useCallback(async () => {
    if (isZipcode) {
      setShowZipcodeError();
      return;
    }
    if (stateValue.price.errors.hasErrors) {
      const stepsStateWithShowPriceErrors = { ...stepsState };
      stepsStateWithShowPriceErrors.price.showPriceErrors = true;
      setStepsState(stepsStateWithShowPriceErrors);
    } else {
      dispatch(
        analyticsActions.sendEvent(
          analyticsEvents.catalogStepContinue({
            from: SoFormStepPrice.nameForData,
            to: SoFormStepComplements.nameForData,
          })
        )
      );
      const stepsStateWithStepComplements = { ...stepsState };
      stepsStateWithStepComplements.complements = {
        isLoading: false,
        //
        add_internal_reference: false,
        internal_reference: '',
        //
        add_comment: false,
        pickup_comment_from_shipper_to_driver: '',
        dropoff_comment_from_shipper_to_driver: '',
        //
        add_insurance: false,
        insured_for: 0,
        //
        add_handling_loading: false,
        add_handling_unloading: false,
        price_per_handling: null,
      };
      stepsStateWithStepComplements.currentStep = SoFormStepComplements;
      setStepsState(stepsStateWithStepComplements);
    }
  }, [
    dispatch,
    isZipcode,
    setShowZipcodeError,
    stateValue.price.errors.hasErrors,
    stepsState,
  ]);

  const onPriceGoBack = useCallback(async () => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepBack({
          from: SoFormStepPrice.nameForData,
          to: SoFormStepConfiguration.nameForData,
        })
      )
    );
    const stepsStateWithStepUpdated = { ...stepsState };
    stepsStateWithStepUpdated.currentStep = SoFormStepConfiguration;
    stepsStateWithStepUpdated.price.selectedProductType = null;
    stepsStateWithStepUpdated.price.candidates = [];
    stepsStateWithStepUpdated.price.showPriceErrors = false;
    setStepsState(stepsStateWithStepUpdated);
  }, [dispatch, stepsState]);

  //
  // complements
  //

  const setAddHandling = useCallback(
    async (value, stopType) => {
      const newState = { ...stepsState };
      if (stopType === 'pickup') {
        newState.complements.add_handling_loading = value;
      } else if (stopType === 'dropoff') {
        newState.complements.add_handling_unloading = value;
      }
      setStepsState(newState);
    },
    [stepsState]
  );

  const setAddInternalReference = useCallback(
    (value) => {
      const newState = { ...stepsState };
      newState.complements.add_internal_reference = value;
      setStepsState(newState);
    },
    [stepsState]
  );

  const setInternalReference = useCallback(
    (value) => {
      const newState = { ...stepsState };
      newState.complements.internal_reference = value;
      setStepsState(newState);
    },
    [stepsState]
  );

  const setAddComment = useCallback(
    (value) => {
      const newState = { ...stepsState };
      newState.complements.add_comment = value;
      setStepsState(newState);
    },
    [stepsState]
  );

  const setComment = useCallback(
    (value, stopType) => {
      const newState = { ...stepsState };

      if (stopType === 'dropoff') {
        newState.complements.dropoff_comment_from_shipper_to_driver = value;
      } else if (stopType === 'pickup') {
        newState.complements.pickup_comment_from_shipper_to_driver = value;
      }
      setStepsState(newState);
    },
    [stepsState]
  );

  const setAddInsurance = useCallback(
    (value) => {
      const newState = { ...stepsState };
      newState.complements.add_insurance = value;
      setStepsState(newState);
    },
    [stepsState]
  );

  const setInsuredFor = useCallback(
    (value) => {
      const nextValue = isNaN(parseInt(value)) ? 0 : parseInt(value);
      const newState = { ...stepsState };
      newState.complements.insured_for = nextValue;
      setStepsState(newState);
    },
    [stepsState]
  );

  const onComplementsSubmit = useCallback(async () => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepContinue({
          from: SoFormStepComplements.nameForData,
          to: SoFormStepConfirmation.nameForData,
        })
      )
    );
    const newState = { ...stepsState };
    newState.complements.isLoading = true;
    setStepsState(newState);
    const selectedProductType = stepsState.price.selectedProductType;
    const candidates = stepsState.price.candidates;
    const selectedCandidate = candidates.find(
      (c) => c.productType === selectedProductType
    );
    const payload = getPayloadWithComplements({
      stepsState,
      candidate: selectedCandidate,
    });
    const quotes = [payload];
    try {
      const response = await api.doQuote(quotes);
      let quoteWithComplements = response.data.quotes.DIRECT;
      if (quoteWithComplements === undefined)
        quoteWithComplements = response.data.quotes.TODAY;
      const newStateSuccess = { ...newState };
      newStateSuccess.complements.isLoading = false;
      newStateSuccess.currentStep = SoFormStepConfirmation;
      newStateSuccess.activeQuote = quoteWithComplements;
      newStateSuccess.activeQuotePayload = payload;
      newStateSuccess.activeQuotePayload.slug = quoteWithComplements.slug;
      newStateSuccess.activeQuotePayload.quote_slug = quoteWithComplements.slug;
      newStateSuccess.activeQuotePayload.terms_and_conditions_version =
        terms_and_conditions_version;

      setStepsState(newStateSuccess);
    } catch (e) {
      captureCatalogError({
        e,
        step_name: SoFormStepComplements.nameForData,
        error_type: 'API',
        action_name: 'onComplementsSubmit doQuote',
        extras: quotes,
      });
    }
  }, [dispatch, stepsState, terms_and_conditions_version]);

  const onComplementsGoBack = useCallback(async () => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepBack({
          from: SoFormStepComplements.nameForData,
          to: SoFormStepPrice.nameForData,
        })
      )
    );
    const stepsStateWithStepUpdated = { ...stepsState };
    stepsStateWithStepUpdated.currentStep = SoFormStepPrice;
    stepsStateWithStepUpdated.price.showPriceErrors = false;
    setStepsState(stepsStateWithStepUpdated);
  }, [dispatch, stepsState]);

  //
  // confirmation
  //

  const paymentMethod = useSelector(authSelectors.getUserPaymentMethod);

  const sendConfirmQuoteDataEvent = useCallback(() => {
    const quote = stepsState.activeQuote;
    const quotePayload = stepsState.activeQuotePayload;
    const shipments = quotePayload.shipments;
    const extras = quotePayload.extras;
    const quote_slug = quotePayload.quote_slug;
    const terms_and_conditions_version =
      quotePayload.terms_and_conditions_version;

    const body = {
      terms_and_conditions_version,
      quote_slug,
      shipments,
      extras,
      repeated_from: null,
      price_type: 'DIRECT',
      internal_price_type: 'DIRECT',
      x_docking_enabled: false,
    };
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.form.shipperFormConfirmed({
          type: orderConstants.DIRECT_ORDER,
          body,
          quote,
        })
      )
    );
  }, [dispatch, stepsState.activeQuote, stepsState.activeQuotePayload]);

  const onConfirmationSubmit = useCallback(async () => {
    const payloadForConfirmation = stepsState.activeQuotePayload;
    const stepsStateWithCConfirmationLoading = { ...stepsState };
    stepsStateWithCConfirmationLoading.confirmation.isLoading = true;
    setStepsState(stepsStateWithCConfirmationLoading);

    const newState = { ...stepsState };
    // if they have payment
    if (!isEmpty(paymentMethod)) {
      //   if is CARD
      //   create the order
      //   create the payment intent
      //   redirect to cardStep, they will finish there
      if (paymentMethod === 'CARD') {
        try {
          dispatch(
            analyticsActions.sendEvent(
              analyticsEvents.catalogStepContinue({
                from: SoFormStepConfirmation.nameForData,
                to: SoFormStepCard.nameForData,
              })
            )
          );
          const data = await confirmQuote(payloadForConfirmation);
          sendConfirmQuoteDataEvent();
          try {
            dispatch(
              analyticsActions.sendEvent(
                analyticsEvents.form.shipperFormCard({
                  type: orderConstants.DIRECT_ORDER,
                })
              )
            );
            const paymentIntentSecret = await initPaymentIntent(data.id);
            newState.card.id = data.id;
            newState.success.id = data.id;
            newState.card.paymentIntentSecret = paymentIntentSecret;
            newState.currentStep = SoFormStepCard;
          } catch (e) {
            captureCatalogError({
              e,
              step_name: SoFormStepConfirmation.nameForData,
              error_type: 'API',
              action_name: 'onConfirmationSubmit initPaymentIntent',
              extras: { data_id: data.id },
            });
          }
        } catch (e) {
          captureCatalogError({
            e,
            step_name: SoFormStepConfirmation.nameForData,
            error_type: 'API',
            action_name: 'onConfirmationSubmit confirmQuote CARD',
            extras: payloadForConfirmation,
          });
        }
      }
      //   if is NOT CARD
      //            create the order
      //              redirect to success
      else {
        try {
          dispatch(
            analyticsActions.sendEvent(
              analyticsEvents.catalogStepContinue({
                from: SoFormStepConfirmation.nameForData,
                to: SoFormStepSuccess.nameForData,
              })
            )
          );
          const data = await confirmQuote(payloadForConfirmation);
          sendConfirmQuoteDataEvent();
          newState.success.id = data.id;
          newState.currentStep = SoFormStepSuccess;
        } catch (e) {
          captureCatalogError({
            e,
            step_name: SoFormStepConfirmation.nameForData,
            error_type: 'API',
            action_name: 'onConfirmationSubmit confirmQuote NOTCARD',
            extras: payloadForConfirmation,
          });
        }
      }
    } else {
      dispatch(
        analyticsActions.sendEvent(analyticsEvents.paymentDataRequestPage())
      );
      dispatch(
        analyticsActions.sendEvent(
          analyticsEvents.catalogStepContinue({
            from: SoFormStepConfirmation.nameForData,
            to: SoFormStepPayment.nameForData,
          })
        )
      );
      newState.currentStep = SoFormStepPayment;
    }
    newState.confirmation.isLoading = false;
    setStepsState(newState);
  }, [dispatch, paymentMethod, sendConfirmQuoteDataEvent, stepsState]);

  const onConfirmationGoBack = useCallback(async () => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepBack({
          from: SoFormStepConfirmation.nameForData,
          to: SoFormStepConfiguration.nameForData,
        })
      )
    );
    const stepsStateWithStepUpdated = { ...stepsState };
    stepsStateWithStepUpdated.currentStep = SoFormStepConfiguration;
    stepsStateWithStepUpdated.price.showPriceErrors = false;
    setStepsState(stepsStateWithStepUpdated);
  }, [dispatch, stepsState]);

  //
  // payment
  //

  const paymentData = useSelector(paymentDataSelectors.getPaymentData);
  const paymentMethodForPayment = useSelector(
    paymentDataSelectors.getPaymentMethod
  );
  const fiscalInfoForPayment = useSelector(paymentDataSelectors.getFiscalInfo);
  const isAllowedToDoShipments = useSelector(
    authSelectors.getPaymentDataAllowShipperDoShipments
  );

  const setPaymentMethod = useCallback(async () => {
    const body = { ...paymentMethodForPayment };
    const paymentResponse = await api.authPut(
      `/shipper-app/shippers/${shipperId}/payment_method/`,
      {
        ...body,
      }
    );
    return paymentResponse;
  }, [paymentMethodForPayment, shipperId]);

  const setFiscalInfo = useCallback(async () => {
    const body = { ...fiscalInfoForPayment };
    const fiscalInfoResponse = await api.authPut(
      `/shipper-app/shippers/${shipperId}/fiscal_info/`,
      {
        ...body,
      }
    );
    return fiscalInfoResponse;
  }, [fiscalInfoForPayment, shipperId]);

  const onPaymentSubmit = useCallback(async () => {
    const loadingState = { ...stepsState };
    loadingState.payment.isLoading = true;
    setStepsState(loadingState);
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.paymentDataRequestPageSubmit({
          data: paymentData,
        })
      )
    );
    const { errors } = validatePaymentData({ paymentData });
    try {
      if (!isEmpty(errors)) {
        throw new Error('Error in validation', JSON.stringify(errors));
      }
      const responsePaymentMethod = await setPaymentMethod();
      const responseFiscalInfo = await setFiscalInfo();

      const responses = [responsePaymentMethod, responseFiscalInfo];

      if (responses.find((response) => !response)) {
        throw new Error('Error in the submit, any response is false');
      }
      dispatch(appActions.updateUser());

      const newPaymentMethod = paymentData.payment_method.payment_method;
      const payloadForConfirmation = stepsState.activeQuotePayload;

      const newState = { ...stepsState };

      // CARD
      if (newPaymentMethod === 'CARD') {
        try {
          dispatch(
            analyticsActions.sendEvent(
              analyticsEvents.catalogStepContinue({
                from: SoFormStepPayment.nameForData,
                to: SoFormStepCard.nameForData,
              })
            )
          );
          const data = await confirmQuote(payloadForConfirmation);
          sendConfirmQuoteDataEvent();

          try {
            dispatch(
              analyticsActions.sendEvent(
                analyticsEvents.form.shipperFormCard({
                  type: orderConstants.DIRECT_ORDER,
                })
              )
            );
            const paymentIntentSecret = await initPaymentIntent(data.id);
            newState.card.id = data.id;
            newState.success.id = data.id;
            newState.card.paymentIntentSecret = paymentIntentSecret;
            newState.currentStep = SoFormStepCard;
          } catch (e) {
            captureCatalogError({
              e,
              step_name: SoFormStepPayment.nameForData,
              error_type: 'API',
              action_name: 'onPaymentSubmit initPaymentIntent',
              extras: { data_id: data.id },
            });
          }
        } catch (e) {
          captureCatalogError({
            e,
            step_name: SoFormStepPayment.nameForData,
            error_type: 'API',
            action_name: 'onPaymentSubmit confirmQuote CARD',
            extras: payloadForConfirmation,
          });
        }
      }
      // NOT CARD
      else {
        try {
          dispatch(
            analyticsActions.sendEvent(
              analyticsEvents.catalogStepContinue({
                from: SoFormStepPayment.nameForData,
                to: SoFormStepSuccess.nameForData,
              })
            )
          );
          const data = await confirmQuote(payloadForConfirmation);
          sendConfirmQuoteDataEvent();
          newState.success.id = data.id;
          newState.currentStep = SoFormStepSuccess;
        } catch (e) {
          captureCatalogError({
            e,
            step_name: SoFormStepPayment.nameForData,
            error_type: 'API',
            action_name: 'onPaymentSubmit confirmQuote NOTCARD',
            extras: payloadForConfirmation,
          });
        }
      }
      setStepsState(newState);
    } catch (e) {
      captureCatalogError({
        e,
        step_name: SoFormStepPayment.nameForData,
        error_type: 'API',
        action_name: 'onPaymentSubmit setPaymentMethod setFiscalInfo',
      });
    }
  }, [
    dispatch,
    paymentData,
    sendConfirmQuoteDataEvent,
    setFiscalInfo,
    setPaymentMethod,
    stepsState,
  ]);

  //
  // card
  //

  const onCardPaymentCompleted = useCallback(async () => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepContinue({
          from: SoFormStepCard.nameForData,
          to: SoFormStepSuccess.nameForData,
        })
      )
    );
    const stepsStateWithStepUpdated = { ...stepsState };
    stepsStateWithStepUpdated.currentStep = SoFormStepSuccess;
    setStepsState(stepsStateWithStepUpdated);
  }, [dispatch, stepsState]);

  //
  // success
  //

  const onSuccessGoToShipments = useCallback(async () => {
    const newState = getInitialStepsState();
    setStepsState(newState);
    dispatch(push(routes.orders.default));
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepContinue({
          from: SoFormStepSuccess.nameForData,
          to: 'orders',
        })
      )
    );
  }, [dispatch]);

  const onSuccessNewOrder = useCallback(async () => {
    const newState = getInitialStepsState();
    setStepsState(newState);
    dispatch(appActions.updateUser());
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.catalogStepContinue({
          from: SoFormStepSuccess.nameForData,
          to: SoFormStepConfiguration.nameForData,
        })
      )
    );
  }, [dispatch]);

  //
  // api
  //

  //   update redux step name
  useEffect(() => {
    const stepName = stepsState.currentStep.nameForData;
    dispatch(uiActions.setCatalogStepName({ newValue: stepName }));
  }, [dispatch, stepsState.currentStep]);

  //   check payment method
  useEffect(() => {
    dispatch(orderActions.checkPaymentMethod());
  }, [dispatch, isAllowedToDoShipments]);

  // when user is updated, it means he created an order, send new form event
  useEffect(() => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.form.started({
          type: orderConstants.DIRECT_ORDER,
          user,
        })
      )
    );
  }, [dispatch, user]);

  const stepsApi = useMemo(
    () => ({
      //   configuration
      hideNoProductsForRoute,
      setAddress,
      addLoad,
      deleteLoad,
      setPalletType,
      setLoadType,
      setLoadField,
      addExtra,
      removeExtra,
      onConfigurationSubmit,
      //   price
      setShowZipcodeError,
      selectProduct,
      setUse6hoursWindow,
      setEconomyRange,
      setExpressHours,
      setFlexHours,
      onPriceSubmit,
      onPriceGoBack,
      //   complements
      setAddHandling,
      setAddInternalReference,
      setInternalReference,
      setAddComment,
      setComment,
      setAddInsurance,
      setInsuredFor,
      onComplementsSubmit,
      onComplementsGoBack,
      //   confirmation
      onConfirmationSubmit,
      onConfirmationGoBack,
      //   payment
      onPaymentSubmit,
      //   card
      onCardPaymentCompleted,
      //   success
      onSuccessNewOrder,
      onSuccessGoToShipments,
    }),
    [
      //   configuration
      hideNoProductsForRoute,
      setAddress,
      addLoad,
      deleteLoad,
      setPalletType,
      setLoadType,
      setLoadField,
      addExtra,
      removeExtra,
      onConfigurationSubmit,
      //   price
      setShowZipcodeError,
      selectProduct,
      setUse6hoursWindow,
      setEconomyRange,
      setExpressHours,
      setFlexHours,
      onPriceSubmit,
      onPriceGoBack,
      //   complements
      setAddHandling,
      setAddInternalReference,
      setInternalReference,
      setAddComment,
      setComment,
      setAddInsurance,
      setInsuredFor,
      onComplementsSubmit,
      onComplementsGoBack,
      //   confirmation
      onConfirmationSubmit,
      onConfirmationGoBack,
      //   payment
      onPaymentSubmit,
      //   card
      onCardPaymentCompleted,
      //   success
      onSuccessNewOrder,
      onSuccessGoToShipments,
    ]
  );

  return (
    <SoFormStepsStateContext.Provider value={stateValue}>
      <SoFormStepsApiContext.Provider value={stepsApi}>
        {children}
      </SoFormStepsApiContext.Provider>
    </SoFormStepsStateContext.Provider>
  );
};
